import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import { motion } from 'framer-motion';

export const CARD_DND_TYPE = 'REALTIME_CARD';

/** A device card that can be dragged to a new position; `index` is its place in the visible grid. */
function DraggableCard({ index, moveCard, onDrop, disabled, children }: any) {
    const ref = useRef<HTMLDivElement>(null);
    const [, drop] = useDrop(
        () => ({
            accept: CARD_DND_TYPE,
            hover(item: any, monitor: any) {
                if (disabled || !ref.current) return;

                const dragIndex = item.index;
                const hoverIndex = index;

                if (dragIndex === hoverIndex) return;

                const hoverRect = ref.current.getBoundingClientRect();
                const hoverMiddleY = (hoverRect.bottom - hoverRect.top) / 2;
                const clientOffset = monitor.getClientOffset();
                if (!clientOffset) return;
                const hoverClientY = clientOffset.y - hoverRect.top;

                if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
                if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

                moveCard(dragIndex, hoverIndex);
                item.index = hoverIndex;
            },
        }),
        [index, moveCard, disabled]
    );

    const [{ isDragging }, drag] = useDrag(
        () => ({
            type: CARD_DND_TYPE,
            item: { index },
            canDrag: !disabled,
            end: () => onDrop?.(),
            collect: (monitor: any) => ({
                isDragging: monitor.isDragging(),
            }),
        }),
        [index, disabled, onDrop]
    );

    drag(drop(ref));

    return (
        <motion.div
            ref={ref}
            layout
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{
                opacity: isDragging ? 0.72 : 1,
                scale: isDragging ? 1.03 : 1,
                y: isDragging ? -2 : 0,
            }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{
                layout: { type: 'spring', stiffness: 420, damping: 34, mass: 0.7 },
                opacity: { duration: 0.18 },
                scale: { type: 'spring', stiffness: 520, damping: 36, mass: 0.6 },
                y: { type: 'spring', stiffness: 460, damping: 34, mass: 0.6 },
            }}
            style={{ willChange: 'transform' }}
        >
            {children}
        </motion.div>
    );
}

export default DraggableCard;
